import { useEffect, useMemo, useState } from 'react';

import { useColorScheme } from '@mui/joy';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePermissionsContext } from '../../../context/permissionsContext';
import { track } from '../../../services/analytics';
import {
  EVENT_DASHBOARD_PAGE,
  EVNTAPP_contact_menu_click,
  EVNTAPP_myprofile_menu_click,
  EVNTAPP_programs_menu_click,
  EVNTAPP_retiree_insights_menu_click,
  EVNTAPP_search_menu_click,
  EVNTAPP_signout_menu_click,
  EVNTAPP_research_hub_menu_click,
  EVNT_nav_upgrade_clicked,
  EVNTAPP_following_menu_click,
} from '../../../services/analytics/events';
import clearfeed from '../../../services/support/clearfeed';
import { useGlobalStore } from '../../../stores/useGlobalStore';
import { UPGRADE_SOURCE } from '../../../utils/constants/upgradeSources';
import useIsCalyxTeamMember from '../../../utils/hooks/useIsCalyxTeamMember';
import useIsMobile from '../../../utils/hooks/useIsMobile';
import { useLogout } from '../../../utils/hooks/useLogout';
import { usePermissions } from '../../../utils/hooks/usePermissions';
import usePreviousRoute from '../../../utils/hooks/usePreviousRoute';
import { APPLE_NAV_PATHS, projectDetailRoutes, RESEARCH_HUB_PATH } from '../constants';

import useFetchFollowedProjectsCount from './useFetchFollowedProjectsCount';

export interface NavItem {
  path: string;
  title: string;
  subtitle?: string;
  isNew?: boolean;
  isDropdown?: boolean;
  items?: Array<NavItem>;
  /** Indented under the item above it in a dropdown (Figma 3458:19512). */
  isSubItem?: boolean;
  isBeta?: boolean;
  event: string;
  hasPermission?: boolean;
  show: boolean;
  activeCallback?: (path: string) => boolean;
}

export interface ProfileMenuItem {
  title: string;
  show: boolean;
  onClick: () => void;
}

export const FOLLOWING_PROJECTS_ON_SEARCH_PAGE = '/credit-ratings?following=true';

export function useNavbarState() {
  const { hasPermission } = usePermissions();
  const { isFreemiumUser, isVersionMismatch, isDemoUser, userData, isAppleClient } =
    usePermissionsContext();
  const hasPermissionForResearchHub = hasPermission('research_hub:research_list');
  const hasResearchHubSubscriberAccess = hasPermission('research_hub:subscriber_only');
  const hasProgramsListPermission = hasPermission('programs:list');
  const hasRetirementInsightsPermission = hasPermission('retirement_insights:list');
  const hasSupplyLinkPermission = hasPermission('supply_bridge:list');
  const hasProjectScreenerPermission = hasPermission('dashboard:project_screener');
  const userInfo = useGlobalStore((s) => s.user);
  const logout = useLogout();
  const { mode, setMode } = useColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const appStateData = useGlobalStore((s) => ({ isStaging: s.isStaging }));
  const setAppState = useGlobalStore((s) => s.setIsStaging);
  const setMiscState = useGlobalStore((s) => s.setMiscState);
  const isCalyxTeamMember = useIsCalyxTeamMember();
  const isMobile = useIsMobile();
  const prevPath = usePreviousRoute();
  const { filterState } = useGlobalStore((s) => ({
    retireeInsightsRedirectUrl: s.retireeInsightsRedirectUrl,
    filterState: s.filterState,
    prevPath: s.prevPath,
  }));
  const isProjectDeveloper = useMemo(() => userInfo?.company_type === 'developer', [userInfo]);

  const [openDropdownPath, setOpenDropdownPath] = useState<string | null>(null);
  const [isHoveringOverProfile, setIsHoveringOverProfile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // ---- Nav items config ---------------------------------------------------
  const allNavItems: Array<NavItem> = [
    {
      // Dev Mode annotations 224:34449 / 224:34453 rename both tabs for Apple.
      title: isAppleClient ? 'Project ratings' : 'Projects',
      path: '/credit-ratings',
      isBeta: false,
      activeCallback: (path) => !!projectDetailRoutes.includes(path),
      event: EVNTAPP_search_menu_click,
      show: true,
    },
    {
      title: isAppleClient ? 'Apple SupplyBridge' : 'SupplyBridge',
      path: '/supply-bridge',
      isBeta: false,
      isNew: false,
      event: '',
      hasPermission: hasSupplyLinkPermission,
      show: true,
    },
    {
      title: 'Diligence tools',
      path: '/diligence-tools',
      isBeta: false,
      show: true,
      event: '',
      isDropdown: true,
      items: [
        {
          title: 'Calyx Compass',
          subtitle: 'Methodology-specific guidance for early-stage and unrated projects',
          path: '/compass',
          isBeta: false,
          show: true,
          event: '',
        },
        {
          title: 'SmartScan',
          subtitle: 'AI-powered screening of your project documentation',
          path: '/project-screener',
          event: '',
          show: true,
          isNew: true,
          hasPermission: hasProjectScreenerPermission,
        },
        {
          title: 'Program screenings',
          subtitle: 'Assessment results of the programs that issue carbon credits',
          path: '/programs',
          event: EVNTAPP_programs_menu_click,
          hasPermission: hasProgramsListPermission,
          show: true,
        },
      ],
    },
    {
      // Figma 3458:19501: the hub, with its topic pages nested under it.
      title: 'Research hub',
      path: RESEARCH_HUB_PATH,
      event: EVNTAPP_research_hub_menu_click,
      hasPermission: hasPermissionForResearchHub,
      show: true,
      isDropdown: true,
      activeCallback: (path) => path.startsWith(RESEARCH_HUB_PATH),
      items: [
        {
          title: 'Research hub',
          subtitle:
            'See the latest research updates, market updates, and articles from our research team',
          path: RESEARCH_HUB_PATH,
          event: EVNTAPP_research_hub_menu_click,
          hasPermission: hasPermissionForResearchHub,
          show: true,
        },
        {
          title: 'CORSIA',
          subtitle: 'Explore the latest CORSIA eligibility updates and research',
          path: `${RESEARCH_HUB_PATH}/corsia`,
          event: '',
          hasPermission: hasResearchHubSubscriberAccess,
          show: true,
          isSubItem: true,
        },
        {
          title: 'ICVCM CCP',
          subtitle: 'Explore the latest ICVCM CCP updates and research',
          path: `${RESEARCH_HUB_PATH}/icvcm-ccp`,
          event: '',
          hasPermission: hasResearchHubSubscriberAccess,
          show: true,
          isSubItem: true,
        },
      ],
    },
    {
      title: 'Retirement insights',
      path: '/retirement_insights',
      event: EVNTAPP_retiree_insights_menu_click,
      hasPermission: hasRetirementInsightsPermission,
      show: true,
    },
    {
      title: 'Developers',
      path: '/developer-portal',
      event: '',
      show: isProjectDeveloper,
    },
  ];

  // Figma 224:34447 draws exactly three tabs, in this order. Research hub stays
  // in-platform: the Apple plan has no `research_hub:research_list` feature, so
  // the page renders its restricted view on its own.
  const navItemsConfig = useMemo<Array<NavItem>>(() => {
    if (!isAppleClient) return allNavItems;
    return APPLE_NAV_PATHS.map((path) => {
      const item = allNavItems.find((navItem) => navItem.path === path);
      if (!item) return null;
      // Apple keeps the plain Research hub tab it had before the dropdown.
      return path === RESEARCH_HUB_PATH
        ? { ...item, hasPermission: true, isDropdown: false, items: undefined }
        : item;
    }).filter((item): item is NavItem => item !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppleClient, allNavItems]);

  // ---- Profile menu config ------------------------------------------------
  const profileMenuConfig: ProfileMenuItem[] = [
    {
      title: 'Settings',
      show: true,
      onClick: () => {
        track(EVNTAPP_myprofile_menu_click, EVENT_DASHBOARD_PAGE);
        window.open(
          process.env.IS_PRODUCTION_APP
            ? 'https://auth.calyxglobal.com/account'
            : 'https://auth.calyxstaging.com/account',
          '_blank'
        );
      },
    },
    {
      title: 'Developer Portal',
      show: !isProjectDeveloper && isFreemiumUser,
      onClick: () => {
        navigate('/developer-portal');
      },
    },
    {
      title: 'My plan',
      show: !isFreemiumUser,
      onClick: () => {
        navigate('/my-plan');
      },
    },
    {
      // Full advisory log + new-request CTA (Figma "My requests").
      title: 'My requests',
      show: !isFreemiumUser,
      onClick: () => {
        navigate('/my-advisory-requests');
      },
    },
    {
      title: 'Contact Us',
      show: !isFreemiumUser,
      onClick: () => {
        track(EVNTAPP_contact_menu_click, EVENT_DASHBOARD_PAGE);
        if (isFreemiumUser) {
          navigate('/contact', {
            state: { entryPoint: 'top nav menu', source: UPGRADE_SOURCE.NAVBAR_CONTACT_US },
          });
        } else {
          clearfeed.open();
        }
      },
    },
    {
      title: 'Log Out',
      show: true,
      onClick: () => {
        track(EVNTAPP_signout_menu_click, EVENT_DASHBOARD_PAGE);
        logout();
      },
    },
  ];

  const themeOptionsList: Array<{ label: string; value: 'light' | 'dark' }> = [
    { label: 'Light Mode', value: 'light' },
    { label: 'Dark Mode', value: 'dark' },
  ];

  const envOptionsList: Array<{ label: string; value: boolean }> = [
    { label: 'Production', value: false },
    { label: 'Staging', value: true },
  ];

  // ---- Handlers -----------------------------------------------------------
  const handleNavItemClick = (event: string) => {
    if (event) track(event, EVENT_DASHBOARD_PAGE);
  };

  const handleThemeSwitch = (m: 'dark' | 'light') => {
    setMode(m);
  };

  const handleEnvToggle = () => {
    setAppState(!appStateData.isStaging);
    window.location.reload();
  };

  const handleUpgradeClick = () => {
    track(EVNT_nav_upgrade_clicked, EVENT_DASHBOARD_PAGE);
    navigate('/upgrade', { state: { source: UPGRADE_SOURCE.NAVBAR_UPGRADE_BUTTON } });
  };

  const handleFollowingClick = () => {
    track(EVNTAPP_following_menu_click, EVENT_DASHBOARD_PAGE);
  };

  // ---- Data fetching ------------------------------------------------------
  const { data: followingProjectsCount, isLoading } = useFetchFollowedProjectsCount();

  useEffect(() => {
    setMiscState({ prevPath: prevPath || '' });
  }, [prevPath]);

  useEffect(() => {
    if (userInfo?.user_id && !isFreemiumUser) {
      clearfeed.boot({
        name: userInfo.name,
        email: userInfo.email,
        hash: userData?.clearfeed_user_hash || '',
      });
    }
  }, [isFreemiumUser, userInfo?.user_id, userData?.clearfeed_user_hash]);

  return {
    // permissions / user
    isFreemiumUser,
    isVersionMismatch,
    isDemoUser,
    userInfo,
    isCalyxTeamMember,
    isMobile,
    isProjectDeveloper,
    // ui state
    mode,
    openDropdownPath,
    setOpenDropdownPath,
    isHoveringOverProfile,
    setIsHoveringOverProfile,
    isDrawerOpen,
    setIsDrawerOpen,
    // config
    navItemsConfig,
    profileMenuConfig,
    themeOptionsList,
    envOptionsList,
    appStateData,
    filterState,
    // data
    followingProjectsCount,
    isLoading,
    // navigation
    location,
    navigate,
    logout,
    // handlers
    handleNavItemClick,
    handleThemeSwitch,
    handleEnvToggle,
    handleUpgradeClick,
    handleFollowingClick,
  };
}
